import { IModule, ModuleContent, ModuleParams } from '../types';
import {
  PdfFont,
  PdfBrush,
  PdfSize,
  ETextAlign,
  PdfRect,
  PageGen,
  PdfColor,
  PdfPen,
  PdfPoint,
} from '../../pdf/pdf';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import { DiffLine, DiffType, DiffMode, CharDiffSegment } from './types';
import { myersDiff } from './myers';

// Merge consecutive same-type char segments into runs for readable rendering.
function mergeSegments(segs: CharDiffSegment[]): CharDiffSegment[] {
  if (segs.length === 0) return [];
  const out: CharDiffSegment[] = [];
  let cur = { ...segs[0] };
  for (let i = 1; i < segs.length; i++) {
    if (segs[i].type === cur.type) {
      cur.value += segs[i].value;
    } else {
      out.push(cur);
      cur = { ...segs[i] };
    }
  }
  out.push(cur);
  return out;
}

function computeLineDiff(leftLines: string[], rightLines: string[]): DiffLine[] {
  const result: DiffLine[] = [];
  const diffs = myersDiff(leftLines, rightLines);

  let leftIdx = 0;
  let rightIdx = 0;

  for (const diff of diffs) {
    if (diff.type === 'equal') {
      result.push({
        lineNumber: leftIdx + 1,
        leftContent: leftLines[leftIdx] || '',
        rightContent: rightLines[rightIdx] || '',
        leftType: 'equal',
        rightType: 'equal',
      });
      leftIdx++;
      rightIdx++;
    } else if (diff.type === 'remove') {
      result.push({
        lineNumber: leftIdx + 1,
        leftContent: leftLines[leftIdx] || '',
        rightContent: '',
        leftType: 'remove',
        rightType: 'equal',
        isModifiedLine: false,
      });
      leftIdx++;
    } else if (diff.type === 'add') {
      result.push({
        lineNumber: rightIdx + 1,
        leftContent: '',
        rightContent: rightLines[rightIdx] || '',
        leftType: 'equal',
        rightType: 'add',
        isModifiedLine: false,
      });
      rightIdx++;
    }
  }

  return result;
}

function computeCharDiff(lineDiffs: DiffLine[]): DiffLine[] {
  const result: DiffLine[] = [];

  for (let i = 0; i < lineDiffs.length; i++) {
    const current = lineDiffs[i];
    const next = lineDiffs[i + 1];

    // Pair adjacent remove+add as a modified line with char-level diff.
    if (current.leftType === 'remove' && next?.rightType === 'add') {
      const leftChars = (current.leftContent || '').split('');
      const rightChars = (next.rightContent || '').split('');
      const rawDiffs = myersDiff(leftChars, rightChars).filter(
        (d) => d.value !== undefined && d.value !== ''
      );
      const charDiffs = mergeSegments(rawDiffs);

      current.rightType = 'add';
      current.isModifiedLine = true;
      current.charDiffs = charDiffs;
      current.rightContent = next.rightContent || '';

      result.push(current);
      i++;
    } else {
      result.push(current);
    }
  }

  return result;
}

export const diffModule: IModule = {
  name: 'diff',

  parse(source: string, attrs: Record<string, string>): ModuleContent {
    // Extract attributes from outer <diff> tag
    const diffTagMatch = /<diff([^>]*)>/.exec(source);
    const tagAttrs: Record<string, string> = {};
    if (diffTagMatch && diffTagMatch[1]) {
      const attrStr = diffTagMatch[1];
      const attrRegex = /([\w-]+)=(?:"([^"]*)"|'([^']*)')/g;
      let match;
      while ((match = attrRegex.exec(attrStr)) !== null) {
        tagAttrs[match[1]] = match[2] || match[3] || '';
      }
    }

    const mergedAttrs = { ...tagAttrs, ...attrs };

    const leftMatch = /<left>([\s\S]*)<\/left>/.exec(source);
    const rightMatch = /<right>([\s\S]*)<\/right>/.exec(source);

    const leftText = leftMatch ? leftMatch[1].trim() : '';
    const rightText = rightMatch ? rightMatch[1].trim() : '';

    const leftLines = leftText.split('\n');
    const rightLines = rightText.split('\n');

    while (leftLines.length > 0 && leftLines[0] === '') leftLines.shift();
    while (rightLines.length > 0 && rightLines[0] === '') rightLines.shift();
    while (leftLines.length > 0 && leftLines[leftLines.length - 1] === '') leftLines.pop();
    while (rightLines.length > 0 && rightLines[rightLines.length - 1] === '') rightLines.pop();

    const lineDiffs = computeLineDiff(leftLines, rightLines);
    const charDiffEnabled = mergedAttrs['char-diff'] !== 'false';
    const diffLines = charDiffEnabled ? computeCharDiff(lineDiffs) : lineDiffs;

    return {
      type: 'diff',
      attrs: {
        ...mergedAttrs,
        mode: mergedAttrs.mode || 'inline',
        fontSize: mergedAttrs['font-size'] || '10',
      },
      content: JSON.stringify(diffLines),
    };
  },

  measure(content: ModuleContent, params: ModuleParams): PdfSize {
    const diffLines: DiffLine[] = JSON.parse(content.content);
    const fontSize = parseFloat(content.attrs.fontSize) || 10;
    const mode = (content.attrs.mode || 'inline') as DiffMode;

    const lineHeight = fontSize * 1.4;
    const charWidth = fontSize * 0.6;
    const gutterWidth = mode === 'split' ? 10 : 0;

    if (mode === 'split') {
      let maxLeftLen = 0;
      let maxRightLen = 0;
      for (const line of diffLines) {
        maxLeftLen = Math.max(maxLeftLen, line.leftContent?.length || 0);
        maxRightLen = Math.max(maxRightLen, line.rightContent?.length || 0);
      }
      const lineNumberWidth = 20;
      const leftColWidth = maxLeftLen * charWidth + lineNumberWidth + 15;
      const rightColWidth = maxRightLen * charWidth + lineNumberWidth + 15;
      const totalWidth = leftColWidth + gutterWidth + rightColWidth;
      const height = diffLines.length * lineHeight + 10;
      return new PdfSize(
        Math.max(parseFloat(content.attrs.width) || totalWidth, 160),
        Math.max(parseFloat(content.attrs.height) || height, 30)
      );
    }

    const lineNumberWidth = 25;
    let maxLineLength = 0;
    for (const line of diffLines) {
      const leftLen = line.leftContent?.length || 0;
      const rightLen = line.rightContent?.length || 0;
      maxLineLength = Math.max(maxLineLength, leftLen, rightLen);
    }
    const contentWidth = maxLineLength * charWidth + lineNumberWidth + 10;
    const height = diffLines.length * lineHeight + 10;
    return new PdfSize(
      Math.max(parseFloat(content.attrs.width) || contentWidth, 80),
      Math.max(parseFloat(content.attrs.height) || height, 30)
    );
  },

  draw(content: ModuleContent, rect: PdfRect, align: ETextAlign, params: ModuleParams): void {
    const page = params.page!;
    if (!page) throw new Error('PageGen required for diff drawing');

    const diffLines: DiffLine[] = JSON.parse(content.content);
    const fontSize = parseFloat(content.attrs.fontSize) || 10;
    const mode = (content.attrs.mode || 'inline') as DiffMode;
    const showLineNumbers = content.attrs['line-numbers'] !== 'false';
    const monoFont = new PdfFont('NotoSansMono', fontSize);

    if (mode === 'split') {
      drawSplitMode(page, diffLines, rect, fontSize, monoFont);
    } else {
      drawInlineMode(page, diffLines, rect, fontSize, monoFont, showLineNumbers);
    }
  },
};

// ── Colours ──────────────────────────────────────────────────────────────────
const equalBg = PdfBrush.White;
const addBg = new PdfBrush(PdfColor.fromRgb(210, 245, 210));
const removeBg = new PdfBrush(PdfColor.fromRgb(250, 215, 215));
const modifiedBg = new PdfBrush(PdfColor.fromRgb(255, 243, 205));
const charAddBg = new PdfBrush(PdfColor.fromRgb(30, 160, 50));
const charRemoveBg = new PdfBrush(PdfColor.fromRgb(200, 30, 40));
const charAddText = PdfBrush.White;
const charRemText = PdfBrush.White;
const grayBrush = new PdfBrush(PdfColor.fromRgb(130, 130, 130));

// ── Inline mode ───────────────────────────────────────────────────────────────
function drawInlineMode(
  page: PageGen,
  diffLines: DiffLine[],
  rect: PdfRect,
  fontSize: number,
  monoFont: PdfFont,
  showLineNumbers: boolean
): void {
  const lineHeight = fontSize * 1.4;
  const lineNumberWidth = showLineNumbers ? 22 : 0;
  const charWidth = fontSize * 0.6;
  const padding = 3;

  let y = rect.top + padding;

  for (let i = 0; i < diffLines.length; i++) {
    const line = diffLines[i];

    // Line-number gutter
    if (showLineNumbers) {
      const lineNumRect = new PdfRect(rect.left, y, lineNumberWidth - 2, lineHeight);
      page.drawString(
        String(i + 1),
        monoFont,
        grayBrush,
        null,
        lineNumRect,
        ETextAlign.CenterRight
      );
    }

    // Background + prefix
    let prefix = ' ';
    let bgBrush = equalBg;

    if (line.isModifiedLine) {
      prefix = '~';
      bgBrush = modifiedBg;
    } else if (line.leftType === 'remove') {
      prefix = '-';
      bgBrush = removeBg;
    } else if (line.rightType === 'add') {
      prefix = '+';
      bgBrush = addBg;
    }

    const contentX = rect.left + lineNumberWidth;
    const contentW = rect.width - lineNumberWidth;
    page.drawRectangle(null, bgBrush, new PdfRect(contentX, y, contentW, lineHeight));

    const prefixRect = new PdfRect(contentX + 2, y, 10, lineHeight);
    page.drawString(prefix, monoFont, grayBrush, null, prefixRect, ETextAlign.CenterLeft);

    const textX = contentX + 14;
    const textW = contentW - 16;

    // Char-diff segments (merged remove+add modified line)
    if (line.charDiffs && line.charDiffs.length > 0) {
      let charX = textX;
      const rightBound = textX + textW;
      for (const seg of line.charDiffs) {
        if (!seg?.value || charX >= rightBound) break;
        const segW = Math.min(seg.value.length * charWidth, rightBound - charX);
        const segRect = new PdfRect(charX, y, segW, lineHeight);
        if (seg.type === 'add') {
          page.drawRectangle(null, charAddBg, segRect);
          page.drawString(seg.value, monoFont, charAddText, null, segRect, ETextAlign.TopLeft);
        } else if (seg.type === 'remove') {
          page.drawRectangle(null, charRemoveBg, segRect);
          page.drawString(seg.value, monoFont, charRemText, null, segRect, ETextAlign.TopLeft);
        } else {
          page.drawString(seg.value, monoFont, PdfBrush.Black, null, segRect, ETextAlign.TopLeft);
        }
        charX += segW;
      }
    } else {
      const content = line.leftContent || line.rightContent || '';
      if (content) {
        const textBrush =
          line.leftType === 'remove'
            ? new PdfBrush(PdfColor.fromRgb(160, 0, 0))
            : line.rightType === 'add'
              ? new PdfBrush(PdfColor.fromRgb(0, 100, 0))
              : PdfBrush.Black;
        page.drawString(
          content,
          monoFont,
          textBrush,
          null,
          new PdfRect(textX, y, textW, lineHeight),
          ETextAlign.TopLeft
        );
      }
    }

    y += lineHeight;
  }
}

// ── Split mode ────────────────────────────────────────────────────────────────
function drawSplitMode(
  page: PageGen,
  diffLines: DiffLine[],
  rect: PdfRect,
  fontSize: number,
  monoFont: PdfFont
): void {
  const lineHeight = fontSize * 1.4;
  const lineNumberWidth = 20;
  const gutterWidth = 10;
  const charWidth = fontSize * 0.6;

  const leftColWidth = rect.width / 2 - gutterWidth / 2;
  const rightColWidth = rect.width / 2 - gutterWidth / 2;

  // Headers
  const headerFont = new PdfFont('NotoSans', fontSize - 1);
  page.drawString(
    'LEFT (Original)',
    headerFont,
    grayBrush,
    null,
    new PdfRect(rect.left, rect.top, leftColWidth, lineHeight),
    ETextAlign.Center
  );
  page.drawString(
    'RIGHT (Modified)',
    headerFont,
    grayBrush,
    null,
    new PdfRect(rect.left + leftColWidth + gutterWidth, rect.top, rightColWidth, lineHeight),
    ETextAlign.Center
  );

  // Divider
  const dividerX = rect.left + leftColWidth + gutterWidth / 2;
  page.drawLine(
    new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5),
    new PdfPoint(dividerX, rect.top),
    new PdfPoint(dividerX, rect.bottom)
  );

  let y = rect.top + lineHeight + 3;

  for (let i = 0; i < diffLines.length; i++) {
    const line = diffLines[i];
    const leftX = rect.left;
    const rightX = rect.left + leftColWidth + gutterWidth;

    // ── Left cell ──
    const leftBg =
      line.leftType === 'remove' ? (line.isModifiedLine ? modifiedBg : removeBg) : equalBg;
    page.drawRectangle(null, leftBg, new PdfRect(leftX, y, leftColWidth, lineHeight));

    if (line.leftContent) {
      // Line number
      page.drawString(
        String(i + 1),
        monoFont,
        grayBrush,
        null,
        new PdfRect(leftX + 2, y, lineNumberWidth - 2, lineHeight),
        ETextAlign.CenterRight
      );

      const textStartX = leftX + lineNumberWidth + 4;
      const textW = leftColWidth - lineNumberWidth - 6;

      // Char diffs: left side shows equal+remove segments only
      if (line.isModifiedLine && line.charDiffs && line.charDiffs.length > 0) {
        let charX = textStartX;
        const rightBound = textStartX + textW;
        for (const seg of line.charDiffs) {
          if (!seg?.value || seg.type === 'add') continue;
          if (charX >= rightBound) break;
          const segW = Math.min(seg.value.length * charWidth, rightBound - charX);
          const segRect = new PdfRect(charX, y, segW, lineHeight);
          if (seg.type === 'remove') {
            page.drawRectangle(null, charRemoveBg, segRect);
            page.drawString(seg.value, monoFont, charRemText, null, segRect, ETextAlign.TopLeft);
          } else {
            page.drawString(seg.value, monoFont, PdfBrush.Black, null, segRect, ETextAlign.TopLeft);
          }
          charX += segW;
        }
      } else {
        const brush =
          line.leftType === 'remove' ? new PdfBrush(PdfColor.fromRgb(160, 0, 0)) : PdfBrush.Black;
        page.drawString(
          line.leftContent,
          monoFont,
          brush,
          null,
          new PdfRect(textStartX, y, textW, lineHeight),
          ETextAlign.TopLeft
        );
      }
    }

    // ── Right cell ──
    const rightBg = line.rightType === 'add' ? (line.isModifiedLine ? modifiedBg : addBg) : equalBg;
    page.drawRectangle(null, rightBg, new PdfRect(rightX, y, rightColWidth, lineHeight));

    if (line.rightContent) {
      // Line number
      page.drawString(
        String(i + 1),
        monoFont,
        grayBrush,
        null,
        new PdfRect(rightX + 2, y, lineNumberWidth - 2, lineHeight),
        ETextAlign.CenterRight
      );

      const textStartX = rightX + lineNumberWidth + 4;
      const textW = rightColWidth - lineNumberWidth - 6;

      // Char diffs: right side shows equal+add segments only
      if (line.isModifiedLine && line.charDiffs && line.charDiffs.length > 0) {
        let charX = textStartX;
        const rightBound = textStartX + textW;
        for (const seg of line.charDiffs) {
          if (!seg?.value || seg.type === 'remove') continue;
          if (charX >= rightBound) break;
          const segW = Math.min(seg.value.length * charWidth, rightBound - charX);
          const segRect = new PdfRect(charX, y, segW, lineHeight);
          if (seg.type === 'add') {
            page.drawRectangle(null, charAddBg, segRect);
            page.drawString(seg.value, monoFont, charAddText, null, segRect, ETextAlign.TopLeft);
          } else {
            page.drawString(seg.value, monoFont, PdfBrush.Black, null, segRect, ETextAlign.TopLeft);
          }
          charX += segW;
        }
      } else {
        const brush =
          line.rightType === 'add' ? new PdfBrush(PdfColor.fromRgb(0, 100, 0)) : PdfBrush.Black;
        page.drawString(
          line.rightContent,
          monoFont,
          brush,
          null,
          new PdfRect(textStartX, y, textW, lineHeight),
          ETextAlign.TopLeft
        );
      }
    }

    y += lineHeight;
  }
}
